<template>
    <div v-if="images.length > 0" class="flex flex-col gap-2">
        <h3 class="font-bold">{{ t('general.reviews.customer_images') }}</h3>

        <div class="grid grid-cols-4 gap-2 lg:grid-cols-8">
            <button
                v-for="(image, index) in thumbnails"
                :key="`customer_image_${image.id}`"
                type="button"
                :class="[isDesktopOnly(index) && 'hidden lg:block', 'relative aspect-square cursor-pointer overflow-hidden rounded-md outline-none']"
                @click="showGallery = true"
            >
                <img
                    :src="croppableThumb(image.thumb_url)"
                    :alt="t('general.reviews.image_alt')"
                    loading="lazy"
                    decoding="async"
                    class="size-full object-cover"
                />

                <!-- Mobile shows 4 thumbnails, desktop 8, so each gets its own "view more" overlay. -->
                <span
                    v-if="index === mobileThumbnailAmount - 1 && remainingMobileImageCount > 0"
                    class="absolute inset-0 flex items-center justify-center bg-black/50 px-1 text-center text-xs text-white underline lg:hidden"
                >
                    {{ t('general.reviews.view_more_images') }}
                </span>

                <span
                    v-if="index === desktopThumbnailAmount - 1 && remainingDesktopImageCount > 0"
                    class="absolute inset-0 hidden items-center justify-center bg-black/50 px-1 text-center text-base text-white underline lg:flex"
                >
                    {{ t('general.reviews.view_more_images') }}
                </span>
            </button>
        </div>

        <Modal v-model:open="showGallery" :title="t('general.reviews.customer_images')" size="lg" fullscreen no-footer>
            <template v-if="isLoadingAllImages">
                <div class="grid grid-cols-2 gap-3 px-3 py-3 sm:grid-cols-3">
                    <div v-for="index in 6" :key="`gallery_skeleton_${index}`" class="aspect-square w-full animate-pulse rounded-md bg-gray-100" />
                </div>
            </template>

            <div v-else class="grid grid-cols-2 gap-3 px-3 py-3 sm:grid-cols-3">
                <button
                    v-for="(image, index) in visibleImages"
                    :key="`gallery_image_${image.id}`"
                    type="button"
                    class="cursor-pointer overflow-hidden rounded-md outline-none"
                    @click="openLightbox(index)"
                >
                    <img
                        :src="croppableThumb(image.thumb_url)"
                        :alt="t('general.reviews.image_alt')"
                        loading="lazy"
                        decoding="async"
                        class="aspect-square w-full rounded-md object-cover"
                    />
                </button>
            </div>

            <div v-if="hasMoreImages" ref="sentinel" class="h-1 w-full" />
        </Modal>

        <ImageLightbox
            v-model:open="lightboxOpen"
            v-model:index="lightboxIndex"
            :images="lightboxImages"
            :alt-text-prefix="t('general.reviews.image_alt')"
        />
    </div>
</template>

<script setup lang="ts">
import ImageLightbox, { type LightboxImage } from '@/components/gallery/ImageLightbox.vue';
import Modal from '@/components/modals/Modal.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { LipscoreProductReviewImageObject } from '@/types/generated';
import ProductReviewImagesController from '@/types/wayfinder/actions/App/Http/Controllers/Api/Products/ProductReviewImagesController';
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

type ProductReviewImagesSectionProps = {
    images: LipscoreProductReviewImageObject[];
};

const props = defineProps<ProductReviewImagesSectionProps>();

// Only this section crops the photos to squares, so it asks the image route not to pad them with white (?square=0).
const croppableThumb = (url: string): string => `${url}${url.includes('?') ? '&' : '?'}square=0`;

const { t } = useI18n();
const productPageStore = useProductPageStore();

const showGallery = ref(false);

// The page ships one image more than the thumbnails, enough to tell whether there are more; the full set loads once the gallery opens.
const images = ref(props.images);
const hasFetchedAllImages = ref(false);
const isLoadingAllImages = ref(false);

const mobileThumbnailAmount = 4;
const desktopThumbnailAmount = 8;
const thumbnails = computed(() => images.value.slice(0, desktopThumbnailAmount));
const isDesktopOnly = (index: number): boolean => index >= mobileThumbnailAmount;
const remainingMobileImageCount = computed(() => images.value.length - mobileThumbnailAmount);
const remainingDesktopImageCount = computed(() => images.value.length - desktopThumbnailAmount);

// Render images in batches as the user scrolls, instead of mounting every <img> up front.
const batchSize = 12;
const visibleCount = ref(batchSize);

const visibleImages = computed(() => images.value.slice(0, visibleCount.value));
const hasMoreImages = computed(() => visibleCount.value < images.value.length);

const lightboxOpen = ref(false);
const lightboxIndex = ref(0);

// Review images arrive as typed own-domain URLs, so they map onto the lightbox's pre-resolved per-type fields.
// All images, so the counter shows the full total; the lightbox only loads those near the active one.
const lightboxImages = computed((): LightboxImage[] =>
    images.value.map((image: LipscoreProductReviewImageObject): LightboxImage => ({
        url: null,
        thumb: image.thumb_url,
        mobile: image.image_url,
        laptop: image.image_url,
        full: image.image_url,
    })),
);

function openLightbox(index: number): void {
    lightboxIndex.value = index;
    lightboxOpen.value = true;
}

const fetchAllImages = async () => {
    if (hasFetchedAllImages.value || !productPageStore.lipscoreProduct) {
        return;
    }

    try {
        const response = await fetch(ProductReviewImagesController.url({ lipscoreId: productPageStore.lipscoreProduct.lipscore_id }));

        if (!response.ok) {
            return;
        }

        images.value = await response.json();
        hasFetchedAllImages.value = true;
    } catch {
        // Network failure, or the request aborted on navigation: keep the highlighted images.
    } finally {
        isLoadingAllImages.value = false;
    }
};

const sentinel = useTemplateRef('sentinel');
let observer: IntersectionObserver | undefined;

const disconnectObserver = () => {
    observer?.disconnect();
    observer = undefined;
};

const observeSentinel = async () => {
    disconnectObserver();
    await nextTick();

    if (!sentinel.value) {
        return;
    }

    observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) {
            return;
        }

        visibleCount.value = Math.min(visibleCount.value + batchSize, images.value.length);
    });

    observer.observe(sentinel.value);
};

watch(showGallery, async isOpen => {
    if (!isOpen) {
        disconnectObserver();
        return;
    }

    visibleCount.value = batchSize;
    isLoadingAllImages.value = !hasFetchedAllImages.value;
    await fetchAllImages();
    observeSentinel();
});

watch(hasMoreImages, stillHasMore => {
    if (!stillHasMore) {
        disconnectObserver();
        return;
    }

    observeSentinel();
});

onBeforeUnmount(disconnectObserver);
</script>
